import React, { useState, useEffect } from 'react';
import { Table, Tag, Space, Button, Modal, Typography, Descriptions, Badge, message } from 'antd';
import api from '../authentication/axiosInstance';
import moment from 'moment';

const { Text, Title } = Typography;

const getDispatchStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'unread':
      return 'default';
    case 'read':
      return 'processing';
    case 'confirmed':
      return 'warning';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const getRequestTypeLabel = (type) => {
  const labels = {
    salting_lots: "Salting Lots",
    plowing_lots: "Plowing Lots",
    emergency_exits: "Emergency Exits",
    loading_docks: "Loading Docks",
    walkways: "Walkways",
    other: "Other",
  };
  return labels[type] || type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const getTypeColor = (type) => {
  switch (type?.toLowerCase()) {
    case 'salting_lots':
      return 'blue';
    case 'plowing_lots':
      return 'green';
    case 'emergency_exits':
      return 'pink';
    case 'loading_docks':
      return 'orange';
    case 'walkways':
      return 'purple';
    case 'other':
      return 'gray';
    default:
      return 'default';
  }
};

const DispatchRequestsTable = () => {
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [requestTypes, setRequestTypes] = useState({});

  const fetchRequestType = async (requestId) => {
    try {
      const response = await api.get(`/request-manager/requests/${requestId}/`);
      return response.data.request_type;
    } catch (error) {
      console.error('Failed to fetch request type:', error);
      return 'other';
    }
  };

  const fetchDispatches = async () => {
    try {
      setLoading(true);
      const response = await api.get('/request-manager/request-dispatches/');
      const dispatchesData = response.data;
      setDispatches(dispatchesData);

      // Fetch request types for all dispatches
      const types = {};
      for (const dispatch of dispatchesData) {
        if (dispatch.regular_request) {
          types[dispatch.id] = await fetchRequestType(dispatch.regular_request);
        } else if (dispatch.snowedge_request) {
          types[dispatch.id] = 'snowedge';
        }
      }
      setRequestTypes(types);
    } catch (error) {
      console.error('Failed to fetch dispatches:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDispatches();
  }, []);

  const handleViewDetails = (dispatch) => {
    setSelectedDispatch(dispatch);
    setModalVisible(true);
  };

  const handleCompleteDispatch = async (id) => {
    try {
      setUpdatingStatus(true);
      await api.patch(`/request-manager/request-dispatches/${id}/`, {
        status: 'completed',
        completed_at: moment().toISOString(),
      });
      message.success('Dispatch marked as completed');
      
      // Update the local state to reflect the change
      setDispatches(prevDispatches => 
        prevDispatches.map(dispatch => 
          dispatch.id === id 
            ? { ...dispatch, status: 'completed', completed_at: moment().toISOString() } 
            : dispatch
        )
      );
      
      // If the completed dispatch is currently selected, update that too
      if (selectedDispatch && selectedDispatch.id === id) {
        setSelectedDispatch({
          ...selectedDispatch,
          status: 'completed',
          completed_at: moment().toISOString()
        });
      }
    } catch (error) {
      console.error('Failed to complete dispatch:', error);
      message.error('Failed to mark dispatch as completed');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const columns = [
    {
      title: 'Dispatch ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      fixed: 'left',
      sorter: (a, b) => a.id - b.id,
      render: (id) => <Text>Dispatch #{id}</Text>,
    },
    {
      title: 'Type',
      key: 'type',
      width: 150,
      render: (_, record) => {
        const type = requestTypes[record.id] || 'other';
        const tagClassName = `request-tag ${type.toLowerCase().replace('_', '-')}`;
        
        return (
          <Tag color={getTypeColor(type)} className={tagClassName}>
            {getRequestTypeLabel(type)}
          </Tag>
        );
      },
      filters: [
        { text: 'Salting Lots', value: 'salting_lots' },
        { text: 'Plowing Lots', value: 'plowing_lots' },
        { text: 'Emergency Exits', value: 'emergency_exits' },
        { text: 'Loading Docks', value: 'loading_docks' },
        { text: 'Walkways', value: 'walkways' },
        { text: 'Other', value: 'other' },
        { text: 'SnowEdge', value: 'snowedge' },
      ],
      onFilter: (value, record) => requestTypes[record.id] === value,
    },
    {
      title: 'Site',
      dataIndex: 'site_name',
      key: 'site_name',
      width: 150,
      ellipsis: true,
      sorter: (a, b) => (a.site_name || '').localeCompare(b.site_name || ''),
      render: (site) => site || <Text type="secondary">N/A</Text>,
    },
    {
      title: 'Staff',
      key: 'staff',
      width: 150,
      render: (_, record) => record.staff_name || 'John Doe', // Default value for demonstration
      sorter: (a, b) => (a.staff_name || '').localeCompare(b.staff_name || ''),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status, record) => (
        <Space>
          <Badge 
            status={getDispatchStatusColor(status)} 
            text={<Text strong style={{ textTransform: 'capitalize' }}>{status}</Text>}
          />
          {status !== 'completed' && status !== 'cancelled' && (
            <Button 
              size="small" 
              type="default"
              ghost
              onClick={(e) => {
                e.stopPropagation();
                handleCompleteDispatch(record.id);
              }}
              loading={updatingStatus && selectedDispatch?.id === record.id}
            >
              Complete
            </Button>
          )}
        </Space>
      ),
      filters: [
        { text: 'Unread', value: 'unread' },
        { text: 'Read', value: 'read' },
        { text: 'Confirmed', value: 'confirmed' },
        { text: 'Completed', value: 'completed' },
        { text: 'Cancelled', value: 'cancelled' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Scheduled for',
      key: 'scheduled_for',
      width: 180,
      render: (_, record) => {
        const date = moment(record.dispatched_at);
        const isToday = date.isSame(moment(), 'day');
        const isYesterday = date.isSame(moment().subtract(1, 'day'), 'day');
        const isWithinWeek = date.isAfter(moment().subtract(7, 'days'));
        
        if (isToday) {
          return `Today at ${date.format('h:mm A')}`;
        } else if (isYesterday) {
          return `Yesterday at ${date.format('h:mm A')}`;
        } else if (isWithinWeek) {
          return `${date.format('ddd')} at ${date.format('h:mm A')}`;
        } else {
          return date.format('MMM D, YYYY h:mm A');
        }
      },
      sorter: (a, b) => moment(a.dispatched_at).unix() - moment(b.dispatched_at).unix(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button 
          type="primary"
          size="small"
          onClick={() => handleViewDetails(record)}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={dispatches}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1200 }}
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} dispatches`,
        }}
        size="middle"
        bordered
        rowClassName={(record) => {
          if (record.status === 'completed') return 'table-row-completed';
          if (record.status === 'cancelled') return 'table-row-cancelled';
          return '';
        }}
      />

      <Modal
        title={<Title level={4}>Dispatch Details</Title>}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedDispatch && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Dispatch ID" span={2}>
              {selectedDispatch.id}
            </Descriptions.Item>
            <Descriptions.Item label="Site">
              {selectedDispatch.site_name}
            </Descriptions.Item>
            <Descriptions.Item label="Staff">
              {selectedDispatch.staff_name}
            </Descriptions.Item>
            <Descriptions.Item label="Status" span={2}>
              <Badge 
                status={getDispatchStatusColor(selectedDispatch.status)} 
                text={selectedDispatch.status}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {moment(selectedDispatch.dispatched_at).format('LLL')}
            </Descriptions.Item>
            {selectedDispatch.completed_at && (
              <Descriptions.Item label="Completed At">
                {moment(selectedDispatch.completed_at).format('LLL')}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Notes" span={2}>
              {selectedDispatch.notes || 'No notes provided'}
            </Descriptions.Item>
            {selectedDispatch.response_notes && (
              <Descriptions.Item label="Response Notes" span={2}>
                {selectedDispatch.response_notes}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default DispatchRequestsTable; 